import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import PageManagerTreeViewItem from './tree-view-item.js';

const HOVER_EXPAND_DELAY_MS = 500;

const PageManagerTreeViewItemContainer = ({
  node,
  treeRef = null,
  isOpen = false,
  onToggle = Function.prototype,
  isDragging = false,
  isDropTarget = false,
  onItemClick = Function.prototype,
  searchQuery = '',
  selectModeEnabled = false,
  selectedItemsMap = {},
  folderSelectionStateById = {},
  onItemSelect = Function.prototype,
}) => {
  const expandTimerRef = useRef(null);

  useEffect(() => {
    if (!node.droppable || isOpen || !isDropTarget || !treeRef?.current) {
      return undefined;
    }

    expandTimerRef.current = setTimeout(() => {
      treeRef.current?.open(node.id);
    }, HOVER_EXPAND_DELAY_MS);

    return () => {
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
        expandTimerRef.current = null;
      }
    };
  }, [isDropTarget, isOpen, node.droppable, node.id, treeRef]);
  const handleClick = useCallback(() => {
    if (node.data) {
      if (node.droppable) {
        onToggle();
      }

      onItemClick(node.data);
    }
  }, [node, onToggle, onItemClick]);

  if (!node.data) {
    return null;
  }

  const item = node.data;
  const isFolder = Boolean(item.isFolder);

  let { iconGlyph } = item;

  if (node.droppable) {
    iconGlyph = isOpen ? 'bottom' : 'small-right-arrow';
  }

  return (
    <PageManagerTreeViewItem
      id={item.id}
      name={item.name}
      title={item.title}
      password={item.password}
      iconGlyph={iconGlyph}
      isActive={item.isActive}
      isFolder={isFolder}
      isDraggable={node.draggable !== false}
      isFolderEmpty={Boolean(item.isFolderEmpty)}
      actions={item.actions}
      isEmpty={isDragging}
      searchQuery={searchQuery}
      selectModeEnabled={selectModeEnabled}
      selectedItemsMap={selectedItemsMap}
      folderSelectionState={folderSelectionStateById[item.id]}
      onItemSelect={onItemSelect}
      onAddPageToFolder={item.onAddPageToFolder}
      onClick={handleClick}
    />
  );
};

PageManagerTreeViewItemContainer.propTypes = {
  treeRef: PropTypes.shape({
    current: PropTypes.shape({
      open: PropTypes.func,
      close: PropTypes.func,
    }),
  }),
  node: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    droppable: PropTypes.bool,
    draggable: PropTypes.bool,
    data: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      title: PropTypes.string,
      password: PropTypes.string,
      iconGlyph: PropTypes.string,
      isActive: PropTypes.bool,
      isFolder: PropTypes.bool,
      isFolderEmpty: PropTypes.bool,
      actions: PropTypes.arrayOf(PropTypes.shape({})),
      onAddPageToFolder: PropTypes.func,
    }),
  }).isRequired,
  selectModeEnabled: PropTypes.bool,
  selectedItemsMap: PropTypes.shape({}),
  folderSelectionStateById: PropTypes.shape({}),
  onItemSelect: PropTypes.func,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  isDragging: PropTypes.bool,
  isDropTarget: PropTypes.bool,
  onItemClick: PropTypes.func,
  searchQuery: PropTypes.string,
};

export default React.memo(PageManagerTreeViewItemContainer);
